import { useEffect } from 'react'
import moment from 'moment'

/**
 * Custom hook to synchronize dates between search context and local component state
 *
 * This hook manages a complex state machine for date synchronization with the following states:
 *
 * STATE MACHINE:
 * 1. INITIAL_SYNC: First sync on component mount
 *    - Syncs search context dates → local state
 *    - Sets hasSyncedInitialDatesRef to true
 *
 * 2. SEARCH_CONTEXT_CHANGED: Search context dates updated externally (navigation, URL, sessionStorage)
 *    - Always syncs search context → local state
 *    - Handles async date loading scenarios
 *
 * 3. USER_SELECTING: Calendar is open, user actively selecting dates
 *    - Blocks sync to prevent interrupting user interaction
 *    - Exceptions: initial sync, search context changes, filter variant on rentals page
 *
 * 4. PROPERTY_FLEXIBILITY: Property variant with flexibility enabled (offsetable !== 0)
 *    - Blocks sync, dates should not be shown when flexibility is active
 *
 * 5. SYNC_TO_LOCAL: Search context has dates that should flow to local state
 *    - Compares dates and syncs if different
 *    - Updates form values immediately
 *
 * 6. CLEAR_LOCAL: Neither search nor local has dates, ensure clean state
 *    - Clears local state if needed
 *
 * EDGE CASES HANDLED:
 * - Navigation with dates in sessionStorage (searchContextChanged detection)
 * - Async date loading after component mount (searchContextChanged detection)
 * - Filter variant auto-expansion with initial dates (shouldAllowSync logic)
 * - Homepage preserves local selections (doesn't overwrite with null from search)
 * - Property variant respects flexibility setting (offsetable check)
 *
 * @param {Object} params - Hook parameters
 * @param {moment|null} params.searchStartDate - Start date from search context
 * @param {moment|null} params.searchEndDate - End date from search context
 * @param {moment|null} params.localStartDate - Local component start date state
 * @param {moment|null} params.localEndDate - Local component end date state
 * @param {Function} params.setLocalStartDate - Setter for local start date
 * @param {Function} params.setLocalEndDate - Setter for local end date
 * @param {Function} params.setFieldValue - Formik field setter
 * @param {boolean} params.isPropertyVariant - Is this the property page variant?
 * @param {number} params.offsetable - Flexibility setting (0 = exact dates, 1-3 = flexible)
 * @param {boolean} params.showCalendar - Is calendar dropdown currently open?
 * @param {boolean} params.isFilterVariant - Is this the filter variant?
 * @param {string} params.routerPathname - Current router pathname
 * @param {React.RefObject} params.hasSyncedInitialDatesRef - Ref tracking if initial sync occurred
 * @param {React.RefObject} params.searchContextDatesRef - Ref tracking previous search context dates
 */
export const useDateSync = ({
  searchStartDate,
  searchEndDate,
  localStartDate,
  localEndDate,
  setLocalStartDate,
  setLocalEndDate,
  setFieldValue,
  isPropertyVariant,
  offsetable,
  showCalendar,
  isFilterVariant,
  routerPathname,
  hasSyncedInitialDatesRef,
  searchContextDatesRef
}) => {
  useEffect(() => {
    // STEP 1: Detect if search context dates changed (navigation, URL updates, sessionStorage)
    const prevStart = searchContextDatesRef.current.startDate
    const prevEnd = searchContextDatesRef.current.endDate

    // Safely compare dates - check if they are moment objects before calling valueOf()
    const searchContextChanged =
      (moment.isMoment(prevStart) && moment.isMoment(searchStartDate)
        ? prevStart.valueOf() !== searchStartDate.valueOf()
        : prevStart !== searchStartDate) ||
      (moment.isMoment(prevEnd) && moment.isMoment(searchEndDate)
        ? prevEnd.valueOf() !== searchEndDate.valueOf()
        : prevEnd !== searchEndDate)

    // Update ref BEFORE checking conditions (ensures next render sees current values)
    searchContextDatesRef.current = {
      startDate: searchStartDate,
      endDate: searchEndDate
    }

    // STEP 2: Determine if we should allow sync during calendar interaction
    // These conditions check different reasons we might need to sync while calendar is open
    const isInitialSync = !hasSyncedInitialDatesRef.current // First render - always sync
    const isFilterVariantOnRentalsPage =
      isFilterVariant && routerPathname === '/vacation-rentals' // Filter modal on rentals page
    const searchHasDates = searchStartDate || searchEndDate // Search context has date data

    // Allow sync if any of these conditions are true:
    // - Initial sync: First render, component needs to load dates from search context
    // - Search context changed: Navigation/URL updated dates externally, override local state
    // - Filter variant on rentals page with dates: Always sync to show saved dates in modal
    const shouldAllowSync =
      isInitialSync ||
      searchContextChanged ||
      (isFilterVariantOnRentalsPage && searchHasDates)

    // Block sync if calendar is open and none of the exceptions apply
    if (showCalendar && !shouldAllowSync) {
      return
    }

    // STEP 3: Property variant with flexibility blocks date sync
    if (isPropertyVariant && offsetable !== 0) {
      return
    }

    // STEP 4: Sync search context → local state
    const localHasDates = localStartDate || localEndDate

    if (searchHasDates) {
      // Check if dates differ - safely handle moment objects
      const startDateChanged =
        (searchStartDate && !localStartDate) ||
        (!searchStartDate && localStartDate) ||
        (moment.isMoment(searchStartDate) &&
          moment.isMoment(localStartDate) &&
          searchStartDate.valueOf() !== localStartDate.valueOf())

      const endDateChanged =
        (searchEndDate && !localEndDate) ||
        (!searchEndDate && localEndDate) ||
        (moment.isMoment(searchEndDate) &&
          moment.isMoment(localEndDate) &&
          searchEndDate.valueOf() !== localEndDate.valueOf())

      // Sync if dates differ, initial sync, search changed, or search has dates but local doesn't
      const shouldSync =
        startDateChanged ||
        endDateChanged ||
        (isInitialSync && searchHasDates) ||
        searchContextChanged ||
        (!localHasDates && searchHasDates)

      if (shouldSync) {
        // Sync dates from search context to local state
        setLocalStartDate(searchStartDate)
        setLocalEndDate(searchEndDate)
        // Update form values immediately to keep Formik in sync
        setFieldValue('startDate', searchStartDate)
        setFieldValue('endDate', searchEndDate)
        hasSyncedInitialDatesRef.current = true
      } else if (isInitialSync) {
        // Mark as synced even if dates match (prevents re-running on next render)
        hasSyncedInitialDatesRef.current = true
      }
    } else if (!localHasDates && hasSyncedInitialDatesRef.current) {
      // STEP 5: Clear local state if neither has dates (after initial sync)
      if (localStartDate !== null || localEndDate !== null) {
        setLocalStartDate(null)
        setLocalEndDate(null)
      }
    } else if (isInitialSync && !localHasDates) {
      // STEP 6: Initial sync with no dates - mark as synced
      hasSyncedInitialDatesRef.current = true
    }
    // NOTE: If local has dates but search doesn't, preserve local dates
    // The separate update effect will sync local → search context
  }, [
    searchStartDate,
    searchEndDate,
    localStartDate,
    localEndDate,
    setLocalStartDate,
    setLocalEndDate,
    setFieldValue,
    isPropertyVariant,
    offsetable,
    showCalendar,
    isFilterVariant,
    routerPathname,
    hasSyncedInitialDatesRef,
    searchContextDatesRef
  ])
}
